import * as bootstrap from 'bootstrap';

export const DEFAULT_CREATE_FORM = {
  risk_framework_type_id: '',
  risk_assessment: '',
  assessment_phase: '',
  description: '',
};

export const DEFAULT_EDIT_FORM = {
  risk_framework_type_id: '',
  risk_assessment: '',
  description: '',
  assessment_phase: '',
};

export function filtered_frameworks() {
  const query = String(this.search_query || "").trim().toLowerCase();
  const filter_mode = String(this.filter_mode || "all");

  return this.frameworks.filter(framework =>
    matches_framework_filter(framework, filter_mode, query)
  );
}

export function matches_framework_filter(framework, filter_mode, query) {
  return (
    framework_matches_status(framework, filter_mode) &&
    framework_matches_query(framework, query)
  );
}

export function framework_matches_status(framework, filter_mode) {
  const status = String(framework.status || "").toLowerCase();

  if (filter_mode === "active") {
    return status === "active";
  }

  if (filter_mode === "inactive") {
    return status !== "active";
  }

  return true;
}

export function framework_matches_query(framework, query) {
  if (!query) {
    return true;
  }

  return framework_search_text(framework).includes(query);
}

export function framework_search_text(framework) {
  return [
    framework.frameworkId,
    framework.type,
    framework.title,
    framework.description,
    framework.status,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function sorted_frameworks() {
  return this.filtered_frameworks
    .slice()
    .sort((first, second) => compare_frameworks(first, second, this.sort_mode));
}

export function compare_frameworks(first, second, sortMode) {
  switch (sortMode) {
    case "name_asc":
      return framework_name(first).localeCompare(framework_name(second));

    case "name_desc":
      return framework_name(second).localeCompare(framework_name(first));

    case "created_asc":
      return framework_created(first) - framework_created(second);

    default:
      return framework_created(second) - framework_created(first);
  }
}

export function framework_name(framework) {
  return String(framework.title || "").toLowerCase();
}

export function framework_created(framework) {
  return new Date(framework.date_created || 0).getTime();
}

export function paginated_frameworks() {
  const start = (this.current_page - 1) * this.page_size;
  return this.sorted_frameworks.slice(start, start + this.page_size);
}

export function total_pages() {
  return Math.max(1, Math.ceil(this.sorted_frameworks.length / this.page_size));
}

export function pagination_label() {
  if (!this.sorted_frameworks.length) return 'Showing 0 results';

  const start = ((this.current_page - 1) * this.page_size) + 1;
  const end = Math.min(this.current_page * this.page_size, this.sorted_frameworks.length);
  return `Showing ${start} to ${end} of ${this.sorted_frameworks.length} results`;
}

export function current_project_id() {
  const route_project_id = Number(this.$route?.query?.project_id);
  if (Number.isFinite(route_project_id) && route_project_id > 0) return route_project_id;
  return 1;
}

export function normalize_risk_framework(item) {
  const id = framework_id(item);

  const risk_framework_normalized = {
    id,
    frameworkId: framework_code(id),
    type: framework_type(item),
    risk_framework_type_id: framework_type_id(item),
    risk_assessment: framework_text(item.risk_assessment),
    title: framework_title(item),
    description: framework_text(item.description),
    status: framework_status(item),
    date_created: framework_text(item.date_created),
    assessment_phase: framework_text(item.assessment_phase),
  };
  return risk_framework_normalized;
}

export function framework_id(item) {
  return item?.id ?? Date.now();
}

export function framework_code(id) {
  return `FR-${String(id).padStart(3, "0")}`;
}

export function framework_type(item) {
  return item?.risk_framework_type?.risk_type ?? "General";
}

export function framework_type_id(item) {
  return item?.risk_framework_type?.id ??
         item?.risk_framework_type_id ??
         "";
}

export function framework_title(item) {
  return item?.risk_assessment ?? "Untitled Risk Framework";
}

export function framework_status(item) {
  return item?.is_active ? "Active" : "Inactive";
}

export function framework_text(value) {
  return value ?? "";
}

export function normalize_assessment_phase(value) {
  return String(value || 'pre').toLowerCase().startsWith('post') ? 'post' : 'pre';
}

export function build_create_framework_payload(form, project_id) {
  const response_payload = {
    risk_framework_type_id: Number(form.risk_framework_type_id),
    risk_assessment: String(form.risk_assessment || '').trim(),
    description: String(form.description || '').trim(),
    is_active: true,
    project_id: Number(project_id || 1),
    assessment_phase: normalize_assessment_phase(form.assessment_phase),
  };
  return response_payload;
}

export function build_edit_framework_payload(form, framework_id, project_id) {
  const response_payload = {
    framework_id,
    risk_framework_type_id: Number(form.risk_framework_type_id),
    risk_assessment: String(form.risk_assessment || '').trim(),
    description: String(form.description || '').trim(),
    assessment_phase: normalize_assessment_phase(form.assessment_phase),
    project_id: Number(project_id || 1),
    is_active: true,
  };
  return response_payload;
}

export function validate_framework_form(form, show_error) {
  return (
    validate_framework_type(form, show_error) &&
    validate_framework_name(form, show_error)
  );
}

export function validate_framework_type(form, show_error) {
  const risk_framework_type_id = Number(form?.risk_framework_type_id);

  if (Number.isFinite(risk_framework_type_id) && risk_framework_type_id > 0) {
    return true;
  }

  show_error("Please select a risk type.");
  return false;
}

export function validate_framework_name(form, show_error) {
  const risk_assessment = String(form?.risk_assessment ?? "").trim();

  if (risk_assessment) {
    return true;
  }

  show_error("Please enter a risk name.");
  return false;
}

export function build_edit_form(framework = {}) {
  const edit_risk_payload = {
    risk_framework_type_id: get_form_value(framework.risk_framework_type_id),
    risk_assessment: get_form_value(framework.risk_assessment),
    description: get_form_value(framework.description),
    assessment_phase: get_form_value(framework.assessment_phase),
  };
  return edit_risk_payload;
  
}

export function get_form_value(value) {
  return value ?? "";
}

export function show_bootstrap_modal(modal_id) {
  const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById(modal_id));
  modal.show();
}

export function next_filter_mode(current_mode) {
  const modes = ['all', 'active', 'inactive'];
  const current_index = modes.indexOf(current_mode);
  return modes[(current_index + 1) % modes.length];
}

export function next_sort_mode(current_mode) {
  const modes = ['updated_desc', 'created_asc', 'name_asc', 'name_desc'];
  const current_index = modes.indexOf(current_mode);
  return modes[(current_index + 1) % modes.length];
}

export function sort_mode_label(sort_mode) {
  const labels = {
    updated_desc: 'Newest first',
    created_asc: 'Oldest first',
    name_asc: 'Name A-Z',
    name_desc: 'Name Z-A',
  };

  return Reflect.get(labels, sort_mode) || 'Newest first';
}



